import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, mergeProps as _mergeProps, Fragment as _Fragment, renderList as _renderList } from "vue"

const _hoisted_1 = {
  class: "disb-wrap",
  "data-cy": "settlement-disbursement-table"
}
const _hoisted_2 = { class: "disb-header" }
const _hoisted_3 = { class: "disb-header-actions" }
const _hoisted_4 = {
  key: 0,
  class: "disb-state",
  "data-cy": "disb-loading"
}
const _hoisted_5 = {
  key: 1,
  class: "disb-state disb-state--error",
  "data-cy": "disb-load-error"
}
const _hoisted_6 = { class: "mb-2" }
const _hoisted_7 = {
  class: "flex items-center gap-1 text-[11px] font-bold tracking-[0.04em] uppercase text-[#747474] mb-2",
  "data-cy": "disb-client-heading"
}
const _hoisted_8 = {
  class: "border border-[#e5e5e5] rounded-md px-[14px] py-3 text-[13px]",
  "data-cy": "disb-client-section"
}
const _hoisted_9 = ["data-shortfall"]
const _hoisted_10 = { class: "flex flex-wrap gap-[18px] text-xs text-[#747474]" }
const _hoisted_11 = { "data-cy": "disb-trust-balance" }
const _hoisted_12 = { "data-cy": "disb-trust-amount" }
const _hoisted_13 = {
  key: 0,
  class: "mt-[6px] text-xs text-error",
  "data-cy": "disb-send-blocked-reason"
}
const _hoisted_14 = { class: "flex items-center gap-4" }
const _hoisted_15 = { class: "flex-[1_1_auto] min-w-0" }
const _hoisted_16 = { class: "disb-recipient" }
const _hoisted_17 = { class: "disb-kind" }
const _hoisted_18 = { class: "flex-[0_1_190px] disb-type" }
const _hoisted_19 = { class: "flex-none ml-auto disb-amount" }
const _hoisted_20 = { class: "flex items-center justify-end gap-1 flex-[0_0_250px]" }
const _hoisted_21 = ["data-outcome", "data-cy"]
const _hoisted_22 = ["tabindex"]
const _hoisted_23 = { "data-cy": "disb-cancel-client-withheld" }
const _hoisted_24 = { "data-cy": "disb-undo-client-withheld" }
const _hoisted_25 = {
  key: 1,
  class: "mt-2"
}
const _hoisted_26 = {
  class: "flex items-center gap-1 text-[11px] font-bold tracking-[0.04em] uppercase text-[#747474] mb-2 mt-[22px]",
  "data-cy": "disb-other-heading"
}
const _hoisted_27 = {
  class: "disb-table",
  "data-cy": "disb-table"
}
const _hoisted_28 = { class: "disb-col-check" }
const _hoisted_29 = ["data-cy"]
const _hoisted_30 = { class: "disb-col-check" }
const _hoisted_31 = { class: "disb-recipient" }
const _hoisted_32 = { class: "disb-kind" }
const _hoisted_33 = { class: "disb-type" }
const _hoisted_34 = {
  key: 1,
  class: "disb-breakdown"
}
const _hoisted_35 = { class: "disb-amount" }
const _hoisted_36 = { class: "disb-status" }
const _hoisted_37 = {
  key: 1,
  class: "disb-submitted"
}
const _hoisted_38 = { style: {"max-width":"260px"} }
const _hoisted_39 = { class: "disb-undo-btn" }
const _hoisted_40 = {
  key: 2,
  class: "disb-recovery-actions"
}
const _hoisted_41 = ["data-cy"]
const _hoisted_42 = {
  key: 0,
  class: "disb-msg-row"
}
const _hoisted_43 = { colspan: "4" }
const _hoisted_44 = ["data-cy"]
const _hoisted_45 = { key: 0 }

import { computed, ref, watch } from 'vue';
import type {
  DisbursementEmailComposition,
  PayabliRowAction,
  PayabliRowState,
  SendClientDisbursementPayload,
} from '@/models/disbursementTypes';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlConfirmationDialog from '@/components/LlConfirmationDialog.vue';
import LlMenu from '@/components/LlMenu.vue';
import QboIcon from '@/components/QboIcon.vue';
import LlIcon from '@/components/LlIcon.vue';
import { formatCurrency } from '@/services/utils';
import { undoBlockedReasonFallback } from '@/utils/disbursementHelpers';
import SendDepositRequestDialog from './SendDepositRequestDialog.vue';
import { useSettlementStore } from '../stores/useSettlementStore';
import { useDisbursements } from '../composables/useDisbursements';

// Brand color for the amber (warning) action buttons. Centralized here — and as a
// CSS var in the scoped styles — so a future Vuetify theme-token migration has one
// place to change per concern.
const AMBER = '#f59e0b';

const EMAIL_NOT_SENT_TOOLTIP =
  "The payment link and QuickBooks check are in place, but the email to the client didn't go out, "
  + "so they may not have the link. Resending doesn't create a new link or touch QuickBooks.";


export default /*@__PURE__*/_defineComponent({
  __name: 'SettlementDisbursementTable',
  setup(__props) {

const undoDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const undoMessage = ref('');

/** Bridges the composable's confirm flow to the dialog (a UI concern owned here). */
async function confirmUndo(recipientLabel: string): Promise<boolean> {
  undoMessage.value = `This will reverse the QuickBooks payment for ${recipientLabel}. Continue?`;
  if (!undoDialog.value) return false;
  return undoDialog.value.open();
}

const manualDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const manualMessage = ref('');

async function confirmRecordManual(recipientLabel: string): Promise<boolean> {
  manualMessage.value =
    `This will write a QuickBooks check for ${recipientLabel} and record the disbursement as `
    + 'already paid outside Payabli. Continue?';
  if (!manualDialog.value) return false;
  return manualDialog.value.open();
}

const cancelDialog = ref<InstanceType<typeof LlConfirmationDialog> | null>(null);
const cancelTitle = ref('');
const cancelMessage = ref('');
const cancelConfirmText = ref('');

// A retry is a different action from the payee's point of view: their link is already dead, and
// only the QuickBooks check is left to reverse.
async function confirmCancel(recipientLabel: string, amount: number): Promise<boolean> {
  if (payabliState.value === 'cancelPending') {
    cancelTitle.value = 'Retry QuickBooks Sync?';
    cancelMessage.value =
      `The ${formatCurrency(amount)} payment link to ${recipientLabel} is already canceled. `
      + 'This retries reversing its QuickBooks check. Continue?';
    cancelConfirmText.value = 'Retry QuickBooks Sync';
  } else {
    cancelTitle.value = 'Cancel Payment Link?';
    cancelMessage.value =
      `This will cancel the ${formatCurrency(amount)} payment link sent to ${recipientLabel}. `
      + "They will no longer be able to use it, and this can't be undone. Continue?";
    cancelConfirmText.value = 'Cancel Link';
  }
  if (!cancelDialog.value) return false;
  return cancelDialog.value.open();
}

const {
  rows,
  loading,
  loadError,
  anySubmitted,
  anyBusy,
  bulkBusy,
  // payabliConnected is deliberately not read here: the backend already folds VR-007 into
  // sendBlockedReason and withdraws the cancel action, so re-deriving it would be a second
  // authority on the same rule.
  payabliEnabled,
  trustBalance,
  clientRow,
  canBulkSubmit,
  canBulkUndo,
  isSelected,
  isCheckable,
  hasActionableRows,
  allSelected,
  someSelected,
  toggleRow,
  toggleAll,
  submitRow,
  undoRow,
  submitSelected,
  undoSelected,
  recordManualDisbursement,
  sendClientDisbursement,
  resendClientDisbursementEmail,
  loadClientDisbursementEmail,
  cancelClientDisbursement,
  clientSending,
  clientError,
  reload,
} = useDisbursements({ confirmUndo, confirmRecordManual, confirmCancel });

const settlementStore = useSettlementStore();
const { handleError } = useSnackbarHandler();
const sendDialogOpen = ref(false);
/** Set only while the dialog is open as a resend; null means an ordinary Send. */
const resendFrom = ref<DisbursementEmailComposition | null>(null);
const resendLoading = ref(false);

/** Null until the settlement and its client row are both loaded — the dialog needs all of it. */
const sendDialogContext = computed(() => {
  const settlement = settlementStore.data;
  const row = clientRow.value;
  if (!settlement?.matterId || !row) return null;
  return {
    matterId: settlement.matterId,
    clientName: row.recipientName,
    // A resend is for the amount already sent, not the live net recovery.
    amount: resendFrom.value ? row.payabli?.amount ?? row.amount : row.amount,
  };
});

function openSend(): void {
  resendFrom.value = null;
  sendDialogOpen.value = true;
}

async function openResend(): Promise<void> {
  if (resendLoading.value) return;
  resendLoading.value = true;
  try {
    const previous = await loadClientDisbursementEmail();
    if (!previous) return;
    resendFrom.value = previous;
    sendDialogOpen.value = true;
  } catch (error) {
    handleError(error);
  } finally {
    resendLoading.value = false;
  }
}

async function onSendSubmit(payload: SendClientDisbursementPayload): Promise<void> {
  try {
    // False means the call was suppressed as a duplicate — nothing was posted, so the modal
    // stays open rather than reading as a send that went out.
    const sent = resendFrom.value
      ? await resendClientDisbursementEmail(payload)
      : await sendClientDisbursement(payload);
    if (sent) sendDialogOpen.value = false;
  } catch {
    // The store already recorded the reason against the client row, which the modal renders.
  }
}

// The dialog only mounts once the settlement resolves. Without this, a click made before then
// would leave the flag set and the modal would spring open on its own when the data arrived.
watch(sendDialogContext, (context) => {
  if (!context) sendDialogOpen.value = false;
});

const PAYABLI_STATE_LABELS: Record<PayabliRowState, string> = {
  notSent: '',
  sent: 'Sent',
  inTransit: 'Funds in Transit',
  submittedManual: 'Submitted',
  disbursed: 'Disbursed',
  cancelPending: 'Cancel Pending',
  returned: 'Returned',
};

const payabliState = computed<PayabliRowState | null>(
  () => clientRow.value?.payabli?.state ?? null,
);
const emailDeliveryPending = computed(() => !!clientRow.value?.payabli?.emailDeliveryPending);

const payabliStateLabel = computed(() => {
  if (emailDeliveryPending.value) return 'Email Not Sent';
  return payabliState.value ? PAYABLI_STATE_LABELS[payabliState.value] : '';
});

/** The backend decides eligibility; the component only renders what it was given. */
const hasPayabliAction = (action: PayabliRowAction): boolean =>
  !!clientRow.value?.payabli?.actions.includes(action);

// Chip colours and icons are MatterFeeRecipientsTab's, so a disbursement reads the same on both
// screens; blue marks a manual disbursement (frd.md UI-019).
const PAYABLI_CHIPS: Record<PayabliRowState, { classes: string; icon: string }> = {
  notSent: { classes: 'bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]', icon: 'mdi-clock-outline' },
  sent: { classes: 'bg-[#f1f5f9] text-[#475569] border-[#e2e8f0]', icon: 'mdi-email-outline' },
  inTransit: { classes: 'bg-[#fef3c7] text-[#92400e] border-[#fde68a]', icon: 'mdi-clock-outline' },
  submittedManual: { classes: 'bg-[#dbeafe] text-[#1e40af] border-[#bfdbfe]', icon: 'mdi-check-circle' },
  disbursed: { classes: 'bg-[#dcfce7] text-[#166534] border-[#bbf7d0]', icon: 'mdi-check-circle' },
  cancelPending: { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-alert-circle' },
  returned: { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-alert-circle' },
};

const PAYABLI_STATE_TOOLTIPS: Partial<Record<PayabliRowState, string>> = {
  sent: 'An email went to the client with a secure link to enter their bank details and request the deposit. Nothing has moved yet.',
  inTransit: 'The client entered their bank details and requested the deposit. Funds are on the way to their account.',
  disbursed: "The deposit has landed in the client's bank account. This disbursement is complete.",
};

const EMAIL_NOT_SENT_CHIP = { classes: 'bg-[#fee2e2] text-[#b91c1c] border-[#fecaca]', icon: 'mdi-email-alert-outline' };
const payabliChip = computed(() =>
  (emailDeliveryPending.value ? EMAIL_NOT_SENT_CHIP : PAYABLI_CHIPS[payabliState.value ?? 'notSent']),
);
const payabliStateTooltip = computed(() => {
  if (emailDeliveryPending.value) return EMAIL_NOT_SENT_TOOLTIP;
  return payabliState.value ? PAYABLI_STATE_TOOLTIPS[payabliState.value] ?? null : null;
});

// Two numbers before a send is possible. Dropped once one has happened: the check is a pre-flight
// for an action no longer on offer, and it keeps evaluating against a balance the send already
// debited.
const showTrustCallout = computed(() => payabliState.value === 'notSent');
const trustShortfall = computed(
  () =>
    !!clientRow.value &&
    (trustBalance.value === null || clientRow.value.amount > trustBalance.value),
);

// sendBlockedReason is rendered inside the callout, so repeating it under the row would show the
// same sentence twice. A transient failure still has to surface: clientRow.state (the busy/error
// overlay) and payabliState (the backend's discriminator) are different axes, so a failed manual
// disbursement leaves state 'error' while payabliState is still 'notSent'.
const clientBlockedReason = computed(() => {
  const row = clientRow.value;
  if (!row) return null;
  if (row.state === 'error') return row.message;
  return showTrustCallout.value ? null : row.message ?? null;
});

return (_ctx: any,_cache: any) => {
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _cache[4] || (_cache[4] = _createElementVNode("span", { class: "disb-title" }, "Disbursements", -1)),
      _createElementVNode("div", _hoisted_3, [
        _createVNode(LlSecondaryButton, {
          color: "primary",
          variant: "outlined",
          size: "small",
          class: "disb-btn",
          "data-cy": "disb-bulk-submit",
          loading: _unref(bulkBusy),
          disabled: !_unref(canBulkSubmit),
          onClick: _unref(submitSelected)
        }, {
          default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
            _createTextVNode(" Submit to QuickBooks ", -1)
          ]))]),
          _: 1
        }, 8, ["loading", "disabled", "onClick"]),
        (_unref(anySubmitted))
          ? (_openBlock(), _createBlock(LlSecondaryButton, {
              key: 0,
              color: AMBER,
              variant: "flat",
              size: "small",
              class: "disb-btn text-white",
              "data-cy": "disb-bulk-undo",
              disabled: !_unref(canBulkUndo),
              title: "Revert submitted disbursements",
              onClick: _unref(undoSelected)
            }, {
              default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                _createTextVNode(" Undo ", -1)
              ]))]),
              _: 1
            }, 8, ["disabled", "onClick"]))
          : _createCommentVNode("", true)
      ])
    ]),
    (_unref(loading) && !_unref(rows).length)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _createVNode(_component_v_progress_circular, {
            indeterminate: "",
            color: "success",
            size: "28"
          })
        ]))
      : (_unref(loadError) && !_unref(rows).length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(loadError)), 1),
            _createVNode(LlSecondaryButton, {
              color: "primary",
              variant: "outlined",
              size: "small",
              class: "disb-btn-sm",
              "data-cy": "disb-reload",
              onClick: _unref(reload)
            }, {
              default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                _createTextVNode(" Retry ", -1)
              ]))]),
              _: 1
            }, 8, ["onClick"])
          ]))
        : (_openBlock(), _createElementBlock(_Fragment, { key: 2 }, [
            (_unref(payabliEnabled) && _unref(clientRow))
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("div", _hoisted_7, [
                    _cache[8] || (_cache[8] = _createTextVNode(" Client Disbursement ", -1)),
                    _createVNode(LlTooltip, { location: "top" }, {
                      activator: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                        _createElementVNode("span", {
                          class: "info-dot",
                          tabindex: "0",
                          "data-cy": "disb-client-info"
                        }, "i", -1)
                      ]))]),
                      default: _withCtx(() => [
                        _cache[7] || (_cache[7] = _createElementVNode("div", { class: "max-w-[280px]" }, " Your client's net recovery, paid by secure payment link. Funds leave trust when the client enters their bank details. ", -1))
                      ]),
                      _: 1
                    })
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    (showTrustCallout.value)
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 0,
                          class: _normalizeClass(["border rounded px-[10px] py-2 mb-3", trustShortfall.value ? 'border-[rgb(var(--v-theme-error))] bg-[rgba(var(--v-theme-error),0.08)]' : 'border-[#e5e5e5] bg-[#fafafa]']),
                          "data-shortfall": trustShortfall.value || undefined,
                          "data-cy": "disb-trust-callout"
                        }, [
                          _createElementVNode("div", _hoisted_10, [
                            _createElementVNode("span", null, [
                              _cache[9] || (_cache[9] = _createTextVNode(" Trust balance: ", -1)),
                              _createElementVNode("strong", _hoisted_11, _toDisplayString(_unref(trustBalance) === null ? '—' : _unref(formatCurrency)(_unref(trustBalance))), 1)
                            ]),
                            _createElementVNode("span", null, [
                              _cache[10] || (_cache[10] = _createTextVNode(" Disbursing: ", -1)),
                              _createElementVNode("strong", _hoisted_12, _toDisplayString(_unref(formatCurrency)(_unref(clientRow).amount)), 1)
                            ])
                          ]),
                          (_unref(clientRow).sendBlockedReason)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_13, _toDisplayString(_unref(clientRow).sendBlockedReason), 1))
                            : _createCommentVNode("", true)
                        ], 10, _hoisted_9))
                      : _createCommentVNode("", true),
                    _createElementVNode("div", _hoisted_14, [
                      _createElementVNode("div", _hoisted_15, [
                        _createElementVNode("div", _hoisted_16, _toDisplayString(_unref(clientRow).recipientName), 1),
                        _createElementVNode("div", _hoisted_17, _toDisplayString(_unref(clientRow).recipientKind), 1)
                      ]),
                      _createElementVNode("div", _hoisted_18, _toDisplayString(_unref(clientRow).typeLabel), 1),
                      _createElementVNode("div", _hoisted_19, _toDisplayString(_unref(formatCurrency)(_unref(clientRow).amount)), 1),
                      _createElementVNode("div", _hoisted_20, [
                        (_unref(clientRow).state === 'in-progress')
                          ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                              key: 0,
                              indeterminate: "",
                              color: "success",
                              size: "18",
                              width: "2",
                              "data-cy": "disb-spinner-client"
                            }))
                          : (payabliState.value === 'notSent')
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                _createVNode(LlSecondaryButton, {
                                  color: "primary",
                                  variant: "outlined",
                                  size: "small",
                                  class: "disb-btn-sm",
                                  disabled: _unref(anyBusy) || !_unref(clientRow).canSend || !sendDialogContext.value,
                                  "data-cy": "disb-send-client",
                                  onClick: openSend
                                }, {
                                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                    _createTextVNode(" Send ", -1)
                                  ]))]),
                                  _: 1
                                }, 8, ["disabled"]),
                                _createVNode(LlMenu, {
                                  location: "bottom end",
                                  items: [{
                    title: 'Record Manual Disbursement',
                    dataCy: 'disb-record-manual',
                    disabled: _unref(anyBusy),
                    action: _unref(recordManualDisbursement),
                  }]
                                }, {
                                  activator: _withCtx(({ props: menuProps }) => [
                                    _createVNode(LlSecondaryButton, _mergeProps(menuProps, {
                                      icon: "mdi-dots-vertical",
                                      variant: "text",
                                      size: "small",
                                      density: "comfortable",
                                      "aria-label": "More client disbursement actions",
                                      "data-cy": "disb-client-menu"
                                    }), null, 16)
                                  ]),
                                  _: 1
                                }, 8, ["items"])
                              ], 64))
                            : (_openBlock(), _createElementBlock("span", {
                                key: 2,
                                class: "inline-flex items-center justify-end gap-2",
                                "data-outcome": payabliState.value === 'disbursed' ? 'success' : payabliState.value === 'returned' ? 'failure' : undefined,
                                "data-cy": `disb-client-state-${payabliState.value}`
                              }, [
                                _createVNode(LlTooltip, {
                                  location: "top",
                                  text: payabliStateTooltip.value ?? undefined
                                }, {
                                  activator: _withCtx(() => [
                                    _createElementVNode("span", {
                                      class: _normalizeClass(["inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold border whitespace-nowrap", payabliChip.value.classes]),
                                      tabindex: payabliStateTooltip.value ? 0 : undefined,
                                      "data-cy": "disb-client-chip"
                                    }, [
                                      _createVNode(LlIcon, {
                                        icon: payabliChip.value.icon,
                                        size: "12"
                                      }, null, 8, ["icon"]),
                                      _createTextVNode(" " + _toDisplayString(payabliStateLabel.value), 1)
                                    ], 10, _hoisted_22)
                                  ]),
                                  _: 1
                                }, 8, ["text"]),
                                (hasPayabliAction('cancel') && payabliState.value === 'cancelPending')
                                  ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                      key: 0,
                                      color: AMBER,
                                      variant: "flat",
                                      size: "small",
                                      class: "disb-btn-sm text-white",
                                      disabled: _unref(anyBusy),
                                      "data-cy": "disb-cancel-client",
                                      onClick: _unref(cancelClientDisbursement)
                                    }, {
                                      default: _withCtx(() => [
                                        _createVNode(QboIcon, {
                                          size: "14",
                                          class: "mr-1"
                                        }),
                                        _cache[12] || (_cache[12] = _createTextVNode("Retry QuickBooks Sync", -1))
                                      ]),
                                      _: 1
                                    }, 8, ["disabled", "onClick"]))
                                  : (hasPayabliAction('cancel'))
                                    ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                        key: 1,
                                        color: AMBER,
                                        variant: "text",
                                        size: "small",
                                        class: "disb-btn-sm",
                                        disabled: _unref(anyBusy),
                                        "data-cy": "disb-cancel-client",
                                        onClick: _unref(cancelClientDisbursement)
                                      }, {
                                        default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                          _createTextVNode("Cancel", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["disabled", "onClick"]))
                                    : (_unref(clientRow).payabli?.cancelBlockedReason)
                                      ? (_openBlock(), _createBlock(LlTooltip, {
                                          key: 2,
                                          location: "top"
                                        }, {
                                          default: _withCtx(() => [
                                            _createTextVNode(_toDisplayString(_unref(clientRow).payabli.cancelBlockedReason), 1)
                                          ]),
                                          activator: _withCtx(() => [
                                            _createElementVNode("span", _hoisted_23, [
                                              _createVNode(LlSecondaryButton, {
                                                color: AMBER,
                                                variant: "text",
                                                size: "small",
                                                class: "disb-btn-sm",
                                                disabled: ""
                                              }, {
                                                default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                                                  _createTextVNode("Cancel", -1)
                                                ]))]),
                                                _: 1
                                              })
                                            ])
                                          ]),
                                          _: 1
                                        }))
                                      : _createCommentVNode("", true),
                                (hasPayabliAction('resendEmail'))
                                  ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                      key: 3,
                                      color: "primary",
                                      variant: "outlined",
                                      size: "small",
                                      class: "disb-btn-sm",
                                      disabled: _unref(anyBusy) || resendLoading.value,
                                      loading: resendLoading.value,
                                      "data-cy": "disb-resend-email-client",
                                      onClick: openResend
                                    }, {
                                      default: _withCtx(() => [...(_cache[15] || (_cache[15] = [
                                        _createTextVNode("Resend Email", -1)
                                      ]))]),
                                      _: 1
                                    }, 8, ["disabled", "loading"]))
                                  : _createCommentVNode("", true),
                                (hasPayabliAction('undo'))
                                  ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                      key: 4,
                                      color: AMBER,
                                      variant: "text",
                                      size: "small",
                                      class: "disb-btn-sm",
                                      disabled: _unref(anyBusy),
                                      "data-cy": "disb-undo-client",
                                      onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(undoRow)(_unref(clientRow))))
                                    }, {
                                      default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                        _createTextVNode("Undo", -1)
                                      ]))]),
                                      _: 1
                                    }, 8, ["disabled"]))
                                  : (payabliState.value === 'submittedManual' && _unref(clientRow).undoBlockedReason)
                                    ? (_openBlock(), _createBlock(LlTooltip, {
                                        key: 5,
                                        location: "top"
                                      }, {
                                        default: _withCtx(() => [
                                          _createTextVNode(_toDisplayString(_unref(clientRow).undoBlockedReason), 1)
                                        ]),
                                        activator: _withCtx(() => [
                                          _createElementVNode("span", _hoisted_24, [
                                            _createVNode(LlSecondaryButton, {
                                              color: AMBER,
                                              variant: "text",
                                              size: "small",
                                              class: "disb-btn-sm",
                                              disabled: ""
                                            }, {
                                              default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                                                _createTextVNode("Undo", -1)
                                              ]))]),
                                              _: 1
                                            })
                                          ])
                                        ]),
                                        _: 1
                                      }))
                                    : _createCommentVNode("", true)
                              ], 8, _hoisted_21))
                      ])
                    ]),
                    (clientBlockedReason.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_25, [
                          _createElementVNode("span", {
                            class: _normalizeClass(["disb-msg", { 'disb-msg--error': _unref(clientRow).state === 'error' }]),
                            "data-cy": "disb-message-client"
                          }, _toDisplayString(clientBlockedReason.value), 3)
                        ]))
                      : _createCommentVNode("", true)
                  ]),
                  _createElementVNode("div", _hoisted_26, [
                    _cache[20] || (_cache[20] = _createTextVNode(" Other Disbursements ", -1)),
                    _createVNode(LlTooltip, { location: "top" }, {
                      activator: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                        _createElementVNode("span", {
                          class: "info-dot",
                          tabindex: "0",
                          "data-cy": "disb-other-info"
                        }, "i", -1)
                      ]))]),
                      default: _withCtx(() => [
                        _cache[19] || (_cache[19] = _createElementVNode("div", { class: "max-w-[280px]" }, " Payees paid directly from trust through QuickBooks — the firm's invoice, lien holders and co-counsel. Select rows to submit them together. ", -1))
                      ]),
                      _: 1
                    })
                  ])
                ], 64))
              : _createCommentVNode("", true),
            _createElementVNode("table", _hoisted_27, [
              _createElementVNode("thead", null, [
                _createElementVNode("tr", null, [
                  _createElementVNode("th", _hoisted_28, [
                    _createVNode(LlCheckbox, {
                      "model-value": _unref(allSelected),
                      indeterminate: _unref(someSelected),
                      "aria-label": "Select all disbursements",
                      "data-cy": "disb-select-all",
                      disabled: !_unref(rows).length || !_unref(hasActionableRows),
                      dense: "",
                      class: "d-inline-flex",
                      "onUpdate:modelValue": _unref(toggleAll)
                    }, null, 8, ["model-value", "indeterminate", "disabled", "onUpdate:modelValue"])
                  ]),
                  _cache[21] || (_cache[21] = _createElementVNode("th", { class: "disb-th text-left" }, "Recipient", -1)),
                  _cache[22] || (_cache[22] = _createElementVNode("th", { class: "disb-th text-left" }, "Type", -1)),
                  _cache[23] || (_cache[23] = _createElementVNode("th", { class: "disb-th text-right" }, "Amount", -1)),
                  _cache[24] || (_cache[24] = _createElementVNode("th", { class: "disb-col-action" }, null, -1))
                ])
              ]),
              _createElementVNode("tbody", null, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(rows), (row) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: row.key
                  }, [
                    _createElementVNode("tr", {
                      class: _normalizeClass(["disb-row", {
              'disb-row--firm': row.key === 'firm',
              'disb-row--submitted': row.state === 'done',
            }]),
                      "data-cy": `disb-row-${row.key}`
                    }, [
                      _createElementVNode("td", _hoisted_30, [
                        _createVNode(LlCheckbox, {
                          "model-value": _unref(isSelected)(row.key),
                          "aria-label": `Select ${row.recipientName}`,
                          "data-cy": `disb-check-${row.key}`,
                          disabled: !_unref(isCheckable)(row),
                          dense: "",
                          class: "d-inline-flex",
                          "onUpdate:modelValue": (checked) => _unref(toggleRow)(row.key, checked)
                        }, null, 8, ["model-value", "aria-label", "data-cy", "disabled", "onUpdate:modelValue"])
                      ]),
                      _createElementVNode("td", null, [
                        _createElementVNode("div", _hoisted_31, _toDisplayString(row.recipientName), 1),
                        _createElementVNode("div", _hoisted_32, _toDisplayString(row.recipientKind), 1)
                      ]),
                      _createElementVNode("td", _hoisted_33, [
                        _createTextVNode(_toDisplayString(row.typeLabel) + " ", 1),
                        (row.feeAmount !== null)
                          ? (_openBlock(), _createBlock(LlTooltip, {
                              key: 0,
                              location: "top"
                            }, {
                              activator: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                _createElementVNode("span", {
                                  class: "info-dot",
                                  tabindex: "0",
                                  "data-cy": "disb-fee-rounding-info"
                                }, "i", -1)
                              ]))]),
                              default: _withCtx(() => [
                                _cache[26] || (_cache[26] = _createElementVNode("div", { style: {"max-width":"260px"} }, " Attorney fees round down to the nearest cent; any leftover goes to your firm's share so the client is never overcharged. ", -1))
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true),
                        (row.feeAmount !== null)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_34, [
                              (row.expenseAmount && row.expenseAmount > 0)
                                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                    _createTextVNode(_toDisplayString(_unref(formatCurrency)(row.feeAmount)) + " fee + " + _toDisplayString(_unref(formatCurrency)(row.expenseAmount)) + " expenses ", 1)
                                  ], 64))
                                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                    _createTextVNode(_toDisplayString(row.percentage?.toFixed(4)) + "% of total fee", 1)
                                  ], 64))
                            ]))
                          : _createCommentVNode("", true),
                        (row.key === 'firm')
                          ? (_openBlock(), _createBlock(LlTooltip, {
                              key: 2,
                              location: "top"
                            }, {
                              activator: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                                _createElementVNode("span", {
                                  class: "info-dot",
                                  tabindex: "0"
                                }, "i", -1)
                              ]))]),
                              default: _withCtx(() => [
                                _cache[28] || (_cache[28] = _createElementVNode("div", { style: {"max-width":"260px"} }, " The firm's invoice (attorney fee + expenses) is finalized and paid from trust automatically when you submit this to QuickBooks. ", -1))
                              ]),
                              _: 1
                            }))
                          : _createCommentVNode("", true)
                      ]),
                      _createElementVNode("td", _hoisted_35, _toDisplayString(_unref(formatCurrency)(row.amount)), 1),
                      _createElementVNode("td", _hoisted_36, [
                        (row.state === 'in-progress')
                          ? (_openBlock(), _createBlock(_component_v_progress_circular, {
                              key: 0,
                              indeterminate: "",
                              color: "success",
                              size: "18",
                              width: "2",
                              "data-cy": `disb-spinner-${row.key}`
                            }, null, 8, ["data-cy"]))
                          : (row.state === 'done')
                            ? (_openBlock(), _createElementBlock("span", _hoisted_37, [
                                _cache[31] || (_cache[31] = _createElementVNode("span", { class: "disb-submitted-label" }, "Submitted", -1)),
                                (row.canUndo)
                                  ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                      key: 0,
                                      color: AMBER,
                                      variant: "flat",
                                      size: "small",
                                      class: "disb-btn-sm text-white disb-undo-btn",
                                      disabled: _unref(anyBusy),
                                      "data-cy": `disb-undo-${row.key}`,
                                      onClick: ($event: any) => (_unref(undoRow)(row))
                                    }, {
                                      default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                                        _createTextVNode(" Undo ", -1)
                                      ]))]),
                                      _: 1
                                    }, 8, ["disabled", "data-cy", "onClick"]))
                                  : (_openBlock(), _createBlock(LlTooltip, {
                                      key: 1,
                                      location: "top"
                                    }, {
                                      activator: _withCtx(() => [
                                        _createElementVNode("span", _hoisted_39, [
                                          _createVNode(LlSecondaryButton, {
                                            color: AMBER,
                                            variant: "flat",
                                            size: "small",
                                            class: "disb-btn-sm text-white",
                                            disabled: "",
                                            "data-cy": `disb-undo-${row.key}`
                                          }, {
                                            default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                              _createTextVNode(" Undo ", -1)
                                            ]))]),
                                            _: 1
                                          }, 8, ["data-cy"])
                                        ])
                                      ]),
                                      default: _withCtx(() => [
                                        _createElementVNode("div", _hoisted_38, _toDisplayString(row.undoBlockedReason || _unref(undoBlockedReasonFallback)(row.key)), 1)
                                      ]),
                                      _: 2
                                    }, 1024))
                              ]))
                            : (row.state === 'error' || row.state === 'needs-recovery')
                              ? (_openBlock(), _createElementBlock("span", _hoisted_40, [
                                  (row.canSubmit)
                                    ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                        key: 0,
                                        color: AMBER,
                                        variant: "flat",
                                        size: "small",
                                        class: "disb-btn-sm text-white",
                                        disabled: _unref(anyBusy),
                                        "data-cy": `disb-retry-${row.key}`,
                                        onClick: ($event: any) => (_unref(submitRow)(row))
                                      }, {
                                        default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                                          _createTextVNode(" Retry ", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["disabled", "data-cy", "onClick"]))
                                    : _createCommentVNode("", true),
                                  (row.canUndo)
                                    ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                        key: 1,
                                        color: AMBER,
                                        variant: "flat",
                                        size: "small",
                                        class: "disb-btn-sm text-white",
                                        disabled: _unref(anyBusy),
                                        "data-cy": `disb-undo-${row.key}`,
                                        onClick: ($event: any) => (_unref(undoRow)(row))
                                      }, {
                                        default: _withCtx(() => [...(_cache[33] || (_cache[33] = [
                                          _createTextVNode(" Undo ", -1)
                                        ]))]),
                                        _: 1
                                      }, 8, ["disabled", "data-cy", "onClick"]))
                                    : _createCommentVNode("", true)
                                ]))
                              : (row.state === 'pending')
                                ? (_openBlock(), _createBlock(LlSecondaryButton, {
                                    key: 3,
                                    color: "primary",
                                    variant: "outlined",
                                    size: "small",
                                    class: "disb-btn-sm",
                                    disabled: _unref(anyBusy),
                                    "data-cy": `disb-submit-${row.key}`,
                                    onClick: ($event: any) => (_unref(submitRow)(row))
                                  }, {
                                    default: _withCtx(() => [...(_cache[34] || (_cache[34] = [
                                      _createTextVNode(" Submit ", -1)
                                    ]))]),
                                    _: 1
                                  }, 8, ["disabled", "data-cy", "onClick"]))
                                : (_openBlock(), _createElementBlock("span", {
                                    key: 4,
                                    class: "disb-dash",
                                    "data-cy": `disb-blocked-${row.key}`
                                  }, " — ", 8, _hoisted_41))
                      ])
                    ], 10, _hoisted_29),
                    (row.message)
                      ? (_openBlock(), _createElementBlock("tr", _hoisted_42, [
                          _cache[35] || (_cache[35] = _createElementVNode("td", null, null, -1)),
                          _createElementVNode("td", _hoisted_43, [
                            _createElementVNode("span", {
                              class: _normalizeClass(["disb-msg", {
                  'disb-msg--error':
                    row.state === 'error' ||
                    row.state === 'needs-recovery' ||
                    (row.state === 'done' && !!row.message),
                }]),
                              "data-cy": `disb-message-${row.key}`
                            }, _toDisplayString(row.message), 11, _hoisted_44)
                          ])
                        ]))
                      : _createCommentVNode("", true)
                  ], 64))
                }), 128)),
                (!_unref(rows).length && !_unref(loading))
                  ? (_openBlock(), _createElementBlock("tr", _hoisted_45, [...(_cache[36] || (_cache[36] = [
                      _createElementVNode("td", {
                        colspan: "5",
                        class: "disb-empty"
                      }, "No disbursements to display.", -1)
                    ]))]))
                  : _createCommentVNode("", true)
              ])
            ])
          ], 64)),
    _createVNode(LlConfirmationDialog, {
      ref_key: "undoDialog",
      ref: undoDialog,
      title: "Reverse QuickBooks Payment?",
      message: undoMessage.value,
      "confirm-text": "Reverse Payment",
      "show-warning-icon": "",
      "data-cy": "disb-undo-dialog"
    }, null, 8, ["message"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "manualDialog",
      ref: manualDialog,
      title: "Record Manual Disbursement?",
      message: manualMessage.value,
      "confirm-text": "Record Disbursement",
      "show-warning-icon": "",
      "data-cy": "disb-manual-dialog"
    }, null, 8, ["message"]),
    _createVNode(LlConfirmationDialog, {
      ref_key: "cancelDialog",
      ref: cancelDialog,
      title: cancelTitle.value,
      message: cancelMessage.value,
      "confirm-text": cancelConfirmText.value,
      "show-warning-icon": "",
      "data-cy": "disb-cancel-dialog"
    }, null, 8, ["title", "message", "confirm-text"]),
    (sendDialogContext.value)
      ? (_openBlock(), _createBlock(SendDepositRequestDialog, _mergeProps({
          key: 3,
          modelValue: sendDialogOpen.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((sendDialogOpen).value = $event))
        }, sendDialogContext.value, {
          "resend-from": resendFrom.value,
          sending: _unref(clientSending),
          "error-message": _unref(clientError),
          onSubmit: onSendSubmit
        }), null, 16, ["modelValue", "resend-from", "sending", "error-message"]))
      : _createCommentVNode("", true)
  ]))
}
}

})